import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Button,
  TextField,
  CircularProgress,
  Box,
  Typography,
  Paper
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { EDUGATE_API_URL, GVA_STATIC_URL } from '../../config/serviceConfig';

const API_BASE_URL = EDUGATE_API_URL;  // EduGate API
const GIN_VUE_URL = GVA_STATIC_URL;   // gin-vue-admin静态文件服务

const NotificationDetail = ({ open, onClose, notification, currentUser, onReplySubmit }) => {
  const { t } = useTranslation();
  const [thread, setThread] = useState([]);
  const [loading, setLoading] = useState(false);
  const [replyContent, setReplyContent] = useState('');
  const [uploadedImageUrl, setUploadedImageUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const contentEndRef = useRef(null);

  // 自动滚动到底部
  useEffect(() => {
    if (contentEndRef.current) {
      contentEndRef.current.scrollIntoView({ behavior: 'auto' }); // 使用 auto 替代 smooth 以减少性能消耗
    }
  }, [thread, loading]);

  // 处理图片URL：将相对路径转换为完整URL
  const processImageUrl = (imageUrl) => {
    if (!imageUrl) return null;
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
      return imageUrl;
    }
    if (imageUrl.startsWith('/')) {
      return `${GIN_VUE_URL}${imageUrl}`;
    } else {
      return `${GIN_VUE_URL}/${imageUrl}`;
    }
  };

  // 获取对话线程
  useEffect(() => {
    if (open && notification) {
      fetchThread();
    }
  }, [open, notification]);

  const fetchThread = async () => {
    if (!notification) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/user/notifications/${notification.id}/thread`);
      const data = await response.json();
      
      if (data.code === 0 && data.data) {
        setThread(data.data);
      }
    } catch (error) {
      console.error('获取对话线程失败:', error);
    } finally {
      setLoading(false);
    }
  };

  // 处理粘贴图片
  const handlePaste = async (e) => {
    const items = e.clipboardData.items;
    for (let item of items) {
      if (item.type.indexOf('image') !== -1) {
        const file = item.getAsFile();
        await uploadImage(file);
      }
    }
  };

  // 上传图片
  const uploadImage = async (file) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const userId = currentUser?.unified_user_id || currentUser?.username;
      const response = await fetch(`${API_BASE_URL}/api/upload/notification-image?user_id=${userId}`, {
        method: 'POST',
        body: formData
      });
      
      const data = await response.json();
      if (data.code === 0 && data.data) {
        setUploadedImageUrl(data.data.url);
      }
    } catch (error) {
      console.error('上传图片失败:', error);
      alert(t('notification_image_upload_failed'));
    } finally {
      setUploading(false);
    }
  };

  // 提交回复
  const handleSubmit = async () => {
    if (!replyContent.trim() && !uploadedImageUrl) {
      return;
    }

    try {
      const userId = currentUser?.unified_user_id || currentUser?.username;
      const userName = currentUser?.name || currentUser?.username;
      
      const response = await fetch(
        `${API_BASE_URL}/api/user/notifications/${notification.id}/reply?user_id=${userId}&user_name=${encodeURIComponent(userName)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            content: replyContent,
            image_url: uploadedImageUrl
          })
        }
      );
      
      const data = await response.json();
      if (data.code === 0) {
        setReplyContent('');
        setUploadedImageUrl(null);
        await fetchThread();
        if (onReplySubmit) {
          onReplySubmit();
        }
      } else {
        alert(t('notification_reply_failed') + ': ' + (data.message || ''));
      }
    } catch (error) {
      console.error('提交回复失败:', error);
        alert(t('notification_reply_submit_failed'));
    }
  };

  // 简单的判断是否是当前用户发送的消息
  const isCurrentUser = (msg) => {
    if (!currentUser) return false;
    const currentUserName = currentUser.name || currentUser.username;
    return msg.sender_name === currentUserName;
  };

  if (!notification) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: '#0f172a', // 纯色背景，低资源消耗
          color: '#e2e8f0',
          borderRadius: '12px',
          border: '1px solid #334155',
          height: '85vh',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      {/* 标题栏 */}
      <DialogTitle sx={{
        bgcolor: '#1e293b', // 纯色背景
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px 24px',
        borderBottom: '1px solid #334155'
      }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#f1f5f9' }}>
            {t('notification_detail_title')}
          </Typography>
          <Typography variant="caption" sx={{ color: '#94a3b8' }}>
            {t('notification_detail_with', { name: notification.sender_name || t('notification_system') })}
          </Typography>
        </Box>
        <IconButton 
          onClick={onClose} 
          size="small" 
          sx={{ 
            color: '#94a3b8',
            '&:hover': { color: '#f1f5f9', bgcolor: 'rgba(255,255,255,0.05)' }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* 对话内容 */}
      <DialogContent sx={{ 
        padding: '24px', 
        bgcolor: '#0f172a',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        overflowY: 'auto'
      }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
            <CircularProgress size={32} sx={{ color: '#3b82f6' }} />
          </Box>
        ) : (
          <>
            {thread.map((msg, index) => {
              const isMe = isCurrentUser(msg);
              return (
                <Box 
                  key={msg.id} 
                  sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    alignItems: isMe ? 'flex-end' : 'flex-start',
                    maxWidth: '85%',
                    alignSelf: isMe ? 'flex-end' : 'flex-start'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, px: 1 }}>
                    <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.75rem' }}>
                      {msg.sender_name} • {new Date(msg.created_at).toLocaleString('zh-CN', {
                        month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit'
                      })}
                    </Typography>
                  </Box>

                  <Paper sx={{
                    p: 2,
                    borderRadius: isMe ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
                    bgcolor: isMe ? '#1d4ed8' : '#1e293b', // 使用纯色而非渐变
                    color: '#f1f5f9',
                    border: '1px solid',
                    borderColor: isMe ? '#1e40af' : '#334155',
                    position: 'relative'
                  }}>
                    {/* 标题（仅首条且非本人） */}
                    {index === 0 && msg.title && !isMe && (
                      <Typography variant="subtitle2" sx={{ 
                        fontWeight: 700, mb: 1, color: '#fff', borderBottom: '1px solid rgba(255,255,255,0.1)', pb: 0.5 
                      }}>
                        {msg.title}
                      </Typography>
                    )}
                    
                    <Typography variant="body2" sx={{ 
                      lineHeight: 1.6, 
                      whiteSpace: 'pre-wrap',
                      fontSize: '0.95rem'
                    }}>
                      {msg.content}
                    </Typography>

                    {msg.image_url && (
                      <Box sx={{ mt: 1.5 }}>
                        <img
                          src={processImageUrl(msg.image_url)}
                          alt={t('notification_attachment')}
                          style={{
                            maxWidth: '100%',
                            maxHeight: '200px',
                            borderRadius: '4px',
                            border: '1px solid rgba(255,255,255,0.1)'
                          }}
                        />
                      </Box>
                    )}
                  </Paper>
                </Box>
              );
            })}
            <div ref={contentEndRef} />
          </>
        )}
      </DialogContent>

      {/* 回复输入区 */}
      <Box sx={{
        bgcolor: '#1e293b',
        borderTop: '1px solid #334155',
        p: 2
      }}>
        {uploadedImageUrl && (
          <Box sx={{ 
            mb: 2, p: 1, 
            display: 'inline-flex', 
            alignItems: 'center', 
            bgcolor: '#0f172a', 
            borderRadius: '6px',
            border: '1px solid #334155'
          }}>
            <img
              src={`http://localhost:8888${uploadedImageUrl}`}
              alt="预览"
              style={{ height: '40px', borderRadius: '4px', marginRight: '8px' }}
            />
            <IconButton size="small" onClick={() => setUploadedImageUrl(null)} sx={{ color: '#ef4444' }}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
           <Box sx={{ flex: 1, position: 'relative' }}>
            <TextField
              multiline
              maxRows={4}
              fullWidth
              placeholder={t('notification_reply_placeholder')}
              value={replyContent}
              onChange={(e) => setReplyContent(e.target.value)}
              onPaste={handlePaste}
              disabled={uploading}
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: '#0f172a',
                  borderRadius: '8px',
                  color: '#f1f5f9',
                  paddingRight: '40px', 
                  '& fieldset': { borderColor: '#334155' },
                  '&:hover fieldset': { borderColor: '#475569' },
                  '&.Mui-focused fieldset': { borderColor: '#3b82f6' }
                },
                '& .MuiInputBase-input::placeholder': {
                  color: '#64748b'
                }
              }}
            />
            <IconButton
              component="label"
              size="small"
              sx={{ 
                position: 'absolute', 
                right: 8, 
                bottom: 8,
                color: '#64748b',
                '&:hover': { color: '#3b82f6' }
              }}
            >
              <input type="file" hidden accept="image/*" onChange={(e) => {
                if (e.target.files[0]) uploadImage(e.target.files[0]);
              }} />
              <ImageOutlinedIcon />
            </IconButton>
          </Box>

          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={uploading || (!replyContent.trim() && !uploadedImageUrl)}
            endIcon={uploading ? <CircularProgress size={16} /> : <SendIcon />}
            sx={{
              height: '56px',
              minWidth: '100px',
              borderRadius: '8px',
              bgcolor: '#3b82f6',
              textTransform: 'none',
              fontSize: '1rem',
              '&:hover': {
                bgcolor: '#2563eb',
              },
              '&.Mui-disabled': {
                bgcolor: '#334155',
                color: '#64748b'
              }
            }}
          >
            {t('notification_send')}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default NotificationDetail;
